export const AGE_OPTIONS = [
  { id: "expecting", label: "Expecting", phrase: "Expecting — and thinking ahead." },
  { id: "0-3", label: "0–3 months", phrase: "Brand new — 0–3 months." },
  { id: "4-11", label: "4–11 months", phrase: "Somewhere in the 4–11 month stretch." },
  { id: "12-23", label: "12–23 months", phrase: "Between one and two." },
  { id: "2-4", label: "2–4 years", phrase: "In the toddler years." },
  { id: "5-plus", label: "5+ years", phrase: "Five or older." },
];

export const HARDEST_OPTIONS = [
  {
    id: "bedtime",
    label: "Bedtime feels impossible",
    fragment: "bedtime",
    ack: { title: "When bedtime takes over the evening, it's hard on everyone.", body: "Let's see what's happening at bedtime." },
  },
  {
    id: "night-waking",
    label: "They're waking throughout the night",
    fragment: "waking during the night",
    ack: { title: "Repeated wake-ups can wear down the whole family.", body: "Let's look at what those wake-ups are like." },
  },
  {
    id: "naps",
    label: "Naps are unpredictable",
    fragment: "naps",
    ack: { title: "Unpredictable naps can make the whole day feel fragile.", body: "Let's look at what's happening with them." },
  },
  {
    id: "need-me",
    label: "They need me to fall back asleep",
    fragment: "needing you to fall back asleep",
    ack: { title: "Doing whatever works at 2 a.m. is not a failure.", body: "Let's look at what those moments are like." },
  },
  {
    id: "unpredictable",
    label: "Everything feels unpredictable",
    fragment: "a rhythm that feels unpredictable",
    ack: { title: "When every day looks different, it's hard to plan anything.", body: "Let's look at the rhythm of your days." },
  },
  {
    id: "advice-overload",
    label: "I'm overwhelmed by conflicting advice",
    fragment: null,
    ack: { title: "There's a lot of advice.", body: "Not all of it applies to your child. Let's narrow down what you're actually dealing with.", big: true },
  },
  {
    id: "dont-know",
    label: "Honestly, I don't know where to start",
    fragment: null,
    ack: { title: "That's okay.", body: "You don't need to know what the problem is before asking for help. You can start with what you're seeing — Andrea can help you sort through the rest.", big: true },
  },
];

const HARDEST_MAP = Object.fromEntries(HARDEST_OPTIONS.map((o) => [o.id, o]));

export const CONTEXT_BRANCHES = {
  night: {
    question: "What usually happens when your child wakes?",
    options: [
      { id: "feeding", label: "Needs feeding", fragment: "they usually need feeding" },
      { id: "rocking", label: "Needs rocking or holding", fragment: "they usually need rocking or holding" },
      { id: "me-nearby", label: "Needs me nearby", fragment: "they need you nearby" },
      { id: "our-bed", label: "Comes into our bed", fragment: "they come into your bed" },
      { id: "long-settle", label: "Takes a long time to settle", fragment: "settling back down takes a long time" },
      { id: "changes", label: "It changes every night", fragment: null },
      { id: "not-sure", label: "I'm not sure", fragment: null },
    ],
  },
  bedtime: {
    question: "What does bedtime feel like right now?",
    options: [
      { id: "long-to-fall", label: "Takes a long time to fall asleep", fragment: "falling asleep takes a long time" },
      { id: "needs-me", label: "Needs me there", fragment: "they need you there" },
      { id: "resistance", label: "Lots of resistance", fragment: "there's a lot of resistance" },
      { id: "multiple-attempts", label: "Multiple attempts", fragment: "it usually takes multiple attempts" },
      { id: "falls-then-wakes", label: "Falls asleep, then wakes again", fragment: "they fall asleep, then wake again" },
      { id: "changes", label: "It changes every night", fragment: null },
      { id: "not-sure", label: "I'm not sure", fragment: null },
    ],
  },
  naps: {
    question: "What's been happening with naps?",
    options: [
      { id: "short-naps", label: "Very short naps", fragment: "naps have been very short" },
      { id: "fighting-naps", label: "Fighting naps", fragment: "they've been fighting naps" },
      { id: "held-naps", label: "Only naps while held", fragment: "they only nap while held" },
      { id: "on-the-go", label: "Only naps on the go", fragment: "they only nap on the go" },
      { id: "unpredictable-naps", label: "Schedule feels unpredictable", fragment: "the nap schedule feels unpredictable" },
      { id: "fine-then-not", label: "Some days are fine, others aren't", fragment: "some days are fine, others aren't" },
      { id: "not-sure", label: "I'm not sure", fragment: null },
    ],
  },
  early: {
    question: "What do the early mornings look like?",
    options: [
      { id: "before-5", label: "Up before 5 a.m.", fragment: "the day is starting very early" },
      { id: "resettle", label: "Needs help resettling", fragment: "they need help resettling" },
      { id: "our-bed", label: "Comes into our bed", fragment: "they come into your bed" },
      { id: "day-starts", label: "Once they're up, the day has started", fragment: "once they're up, the day has started" },
      { id: "changes", label: "It changes every morning", fragment: null },
      { id: "not-sure", label: "I'm not sure", fragment: null },
    ],
  },
  sleep: {
    question: "What does falling asleep look like right now?",
    options: [
      { id: "long-to-fall", label: "Takes a long time", fragment: "falling asleep takes a long time" },
      { id: "needs-me", label: "Needs me there", fragment: "they need you there" },
      { id: "feeding-rocking", label: "Needs feeding or rocking", fragment: "it usually involves feeding or rocking" },
      { id: "changes", label: "It changes every night", fragment: null },
      { id: "not-sure", label: "I'm not sure", fragment: null },
    ],
  },
  rhythm: {
    question: "What feels most unpredictable?",
    options: [
      { id: "up-naps", label: "Nap times", fragment: "nap times change constantly" },
      { id: "up-bedtime", label: "Bedtime", fragment: "bedtime changes constantly" },
      { id: "up-nights", label: "Night wake-ups", fragment: "night wake-ups change constantly" },
      { id: "all-of-it", label: "All of it", fragment: "all of it feels unpredictable" },
      { id: "not-sure", label: "I'm not sure", fragment: null },
    ],
  },
  seeing: {
    question: "What are you seeing most?",
    options: [
      { id: "hardest-bedtime", label: "Hard bedtimes", fragment: "bedtimes are the hardest" },
      { id: "hardest-nights", label: "Hard nights", fragment: "nights are the hardest" },
      { id: "hardest-naps", label: "Hard naps", fragment: "naps are the hardest" },
      { id: "hardest-mornings", label: "Early mornings", fragment: "early mornings are the hardest" },
      { id: "day-to-day", label: "It changes day to day", fragment: "it changes day to day" },
      { id: "not-sure", label: "I'm not sure", fragment: null },
    ],
  },
};

const BRANCH_BY_HARDEST = {
  bedtime: "bedtime",
  "night-waking": "night",
  naps: "naps",
  "need-me": "night",
  unpredictable: "rhythm",
  "advice-overload": "seeing",
  "dont-know": "seeing",
};

export const branchFor = (hardestId) => BRANCH_BY_HARDEST[hardestId] || "seeing";

export const ackForHardest = (hardest) => {
  if (!hardest.length) return null;
  const first = hardest[0];
  return HARDEST_MAP[first.id]?.ack || null;
};

export const EMOTIONAL_OPTIONS = [
  { id: "conflicting", label: "There's too much conflicting advice.", fragment: "you've been sorting through too much conflicting advice" },
  { id: "bad-habits", label: "I'm worried I'm creating bad habits.", fragment: "there's a worry about creating bad habits" },
  { id: "wrong-thing", label: "I'm nervous I'm doing the wrong thing.", fragment: "you're nervous about doing the wrong thing" },
  { id: "stopped-working", label: "What used to work stopped working.", fragment: "what used to work has stopped working" },
  { id: "partner-unsure", label: "My partner and I aren't sure what to try.", fragment: "you and your partner aren't sure what to try" },
  { id: "judged", label: "I feel judged no matter what I choose.", fragment: "it can feel like you're being judged no matter what you choose" },
  { id: "dont-start", label: "I don't even know where to start.", fragment: "it's hard to know where to start" },
  { id: "exhausted", label: "We're exhausted.", fragment: "you're exhausted" },
  { id: "several", label: "Honestly, several of these.", fragment: "it's several of these at once" },
];

const EMOTIONAL_MAP = Object.fromEntries(EMOTIONAL_OPTIONS.map((o) => [o.id, o]));

const EMOTIONAL_RESPONSES = {
  judged: {
    id: "judged",
    title: "You're not here to be graded.",
    body: "This isn't about proving you're doing sleep \u201cright.\u201d It's about understanding your child, your family, and what you are comfortable changing. Andrea starts there.",
    big: true,
  },
  "wrong-thing": {
    id: "wrong-thing",
    title: "You're not here to be graded.",
    body: "This is about understanding what's happening and finding an approach that fits your family.",
    big: true,
  },
  conflicting: {
    id: "conflicting",
    title: "More advice isn't always the answer.",
    body: "Sometimes the missing piece is understanding what actually applies to your child.",
  },
  "bad-habits": {
    id: "bad-habits",
    title: "You don't have to label everything a \u201cbad habit.\u201d",
    body: "Start with what's happening now, what isn't working for your family, and what you'd like to feel different.",
  },
};

export const pickEmotionalResponse = (emotionalIds) => {
  for (const id of ["judged", "wrong-thing", "conflicting", "bad-habits"]) {
    if (emotionalIds.includes(id)) return EMOTIONAL_RESPONSES[id];
  }
  return null;
};

export const INTENT_ACK = {
  title: "You've probably tried a lot.",
  body: "More advice isn't always what helps. Sometimes the missing piece is understanding what actually applies to your child.",
};

export const INTENT_OPTIONS = [
  { id: "managing", label: "We're managing, but I'd like guidance.", phrase: "You're managing, but you'd like guidance." },
  { id: "better-plan", label: "We need a better plan.", phrase: "You need a better plan." },
  { id: "tried-everything", label: "We've tried almost everything.", phrase: "You've tried almost everything." },
  { id: "help-now", label: "We need help now.", phrase: "You need help now." },
];

const joinEnglish = (items) => {
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};

export const buildSummaryParts = (a) => {
  const parts = [];

  const ageOpt = AGE_OPTIONS.find((o) => o.id === a.age);
  if (ageOpt) {
    parts.push({
      pre: a.age === "expecting" ? "Right now, you're" : "Your little one is",
      em: ageOpt.phrase,
    });
  }

  const frags = a.hardest.map((h) => HARDEST_MAP[h.id]?.fragment).filter(Boolean);
  const unsure = a.hardest.some((h) => h.id === "dont-know");
  const overloaded = a.hardest.some((h) => h.id === "advice-overload");
  if (frags.length === 1) {
    parts.push({ pre: "The hardest part right now is", em: frags[0] + "." });
  } else if (frags.length > 1) {
    parts.push({ pre: "The hardest parts right now are", em: joinEnglish(frags) + "." });
  }
  if (unsure) {
    parts.push({ pre: "And honestly,", em: "you don't know where to start", post: "— and that's okay." });
  }
  if (overloaded) {
    parts.push({ pre: "And you're", em: "overwhelmed by conflicting advice." });
  }

  const cfrags = a.context.map((c) => c.fragment).filter(Boolean);
  if (cfrags.length) {
    parts.push({ pre: "When it happens,", em: joinEnglish(cfrags) + "." });
  }

  const efrags = a.emotional.map((e) => EMOTIONAL_MAP[e.id]?.fragment).filter(Boolean);
  if (efrags.length) {
    parts.push({ pre: "And on top of it all —", em: joinEnglish(efrags) + "." });
  }

  const intentOpt = INTENT_OPTIONS.find((o) => o.id === a.intent);
  if (intentOpt) {
    parts.push({ pre: null, em: intentOpt.phrase });
  }

  return parts;
};
